import apiClient, { handleApiResponse, handleApiError } from './api';
import type {
  ApiResponse,
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  OTPVerification,
  OTPSendRequest,
  MFAVerifyRequest,
  MFASetupResponse,
  MFAEnableRequest
} from '../types';

class AuthService {
  /**
   * Register new agency
   */
  async register(credentials: RegisterCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<AuthResponse>>(
        '/agency/auth/register',
        credentials
      );
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Login agency
   * (Includes MFA: returns requiresMFA if MFA is enabled)
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<AuthResponse>>(
        '/agency/auth/login',
        credentials
      );

      const data = handleApiResponse(response);

      // Check if response has flags (either at top level or in data)
      const requiresMFA = data.requiresMFA || data.data?.requiresMFA;
      const requiresPasswordChange = data.requires_password_change || data.data?.requires_password_change;

      // If MFA is required → do NOT store token yet
      if (data.success && !requiresMFA && data.data?.accessToken) {
        localStorage.setItem('agency_token', data.data.accessToken);
        localStorage.setItem('user_type', 'agency'); // Set user type for token interceptor

        // Ensure requires_password_change is in the agency object for persistence
        const agency = data.data.agency;
        if (agency && requiresPasswordChange !== undefined) {
          agency.requires_password_change = requiresPasswordChange;
        }
        localStorage.setItem('agency_user', JSON.stringify(agency));
      }

      // Normalize response structure for frontend
      return {
        success: data.success,
        message: data.message,
        data: {
          ...data.data,
          requiresMFA: requiresMFA,
          requires_password_change: requiresPasswordChange,
          // Ensure agencyId is available for MFA flow
          agencyId: data.data?.agencyId || data.data?.agency?._id,
        }
      };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        '/agency/auth/change-password',
        { oldPassword, newPassword }
      );

      const result = handleApiResponse(response);

      if (result.success) {
        const user = this.getCurrentUser();
        if (user) {
          user.requires_password_change = false;
          this.updateCurrentUser(user);
        }
      }

      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Send OTP to phone
   */
  async sendPhoneOTP(data: OTPSendRequest): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        '/agency/auth/send-phone-otp',
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Verify phone with OTP
   */
  async verifyPhone(data: OTPVerification): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        '/agency/auth/verify-phone',
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Setup MFA (Generate QR Code)
   */
  async setupMFA(): Promise<ApiResponse<MFASetupResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<MFASetupResponse>>(
        '/agency/auth/mfa/setup'
      );
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Enable MFA after scanning QR
   */
  async enableMFA(data: MFAEnableRequest): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        '/agency/auth/mfa/enable',
        data
      );
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Verify MFA token (Login Step 2)
   */
  async verifyMFA(data: MFAVerifyRequest): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await apiClient.post<ApiResponse<AuthResponse>>(
        '/agency/auth/mfa/verify',
        data
      );

      const result = handleApiResponse(response);

      // Store token and user data after successful MFA verification
      if (result.success && result.data && result.data.accessToken) {
        localStorage.setItem('agency_token', result.data.accessToken);
        localStorage.setItem('user_type', 'agency'); // ✅ Set user type for token interceptor

        const agency = result.data.agency;
        // The verify-mfa endpoint also returns requires_password_change
        if (agency && result.data.requires_password_change !== undefined) {
          agency.requires_password_change = result.data.requires_password_change;
        }

        localStorage.setItem('agency_user', JSON.stringify(agency));
      }

      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Disable MFA
   */
  async disableMFA(password: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        '/agency/auth/mfa/disable',
        { password }
      );
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }


  /**
   * Logout agency
   */
  logout(): void {
    localStorage.removeItem('agency_token');
    localStorage.removeItem('agency_user');
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('agency_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Update current user in local storage
   */
  updateCurrentUser(user: any): void {
    localStorage.setItem('agency_user', JSON.stringify(user));
  }

  /**
   * Get token
   */
  getToken(): string | null {
    return localStorage.getItem('agency_token');
  }

  /**
   * Is authenticated?
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

const authService = new AuthService();
export default authService;